import { ChevronRight, Home } from 'lucide-react';
import { useNavigate } from 'react-router';

export interface BreadcrumbItem {
  label: string;
  icon?: React.ElementType;
  to?: string;
  /** Badge text shown after the label, e.g. a proposal number */
  badge?: string;
  badgeColor?: 'violet' | 'cyan' | 'amber' | 'emerald' | 'rose';
}

interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  className?: string;
}

const BADGE_COLORS: Record<NonNullable<BreadcrumbItem['badgeColor']>, string> = {
  violet:  'bg-violet-500/15 text-violet-400 border-violet-500/20',
  cyan:    'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  amber:   'bg-amber-500/15 text-amber-400 border-amber-500/20',
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  rose:    'bg-rose-500/15 text-rose-400 border-rose-500/20',
};

export default function BreadcrumbNav({ items, className = '' }: BreadcrumbNavProps) {
  const navigate = useNavigate();

  return (
    <nav aria-label="Breadcrumb" className={`flex items-center gap-1 flex-wrap ${className}`}>
      {/* Home always first */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center justify-center w-6 h-6 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
      >
        <Home className="w-3.5 h-3.5" />
      </button>
      <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />

      {items.map((item, i) => {
        const Icon = item.icon;
        const isLast = i === items.length - 1;

        return (
          <span key={i} className="flex items-center gap-1">
            <button
              onClick={() => item.to && navigate(item.to)}
              disabled={!item.to || isLast}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                isLast
                  ? 'text-slate-200 bg-slate-800/30 cursor-default'
                  : item.to
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 cursor-pointer'
                  : 'text-slate-500 cursor-default'
              }`}
            >
              {Icon && <Icon className="w-3 h-3" />}
              {item.label}
              {item.badge && (
                <span className={`px-1.5 py-0 rounded border text-[10px] font-bold ${BADGE_COLORS[item.badgeColor ?? 'violet']}`}>
                  {item.badge}
                </span>
              )}
            </button>

            {!isLast && (
              <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
            )}
          </span>
        );
      })}
    </nav>
  );
}
